import type { RouteRecordRaw } from 'vue-router';
import type { HydrationContext, SystemEntityStore, WorkspaceEntityStore, WorkspaceHydration, WorkspaceRuntime } from '#layers/runtime/public';
import { createError } from '#app/composables/error';
import { currentUserRolesHydration, entitlementResourcesHydration } from '#layers/access/public';
import { workspaceBlocksHydration } from '#layers/block-engine/public';
import { workspaceDataConnectorsHydration, workspaceExtensionsHydration } from '#layers/extensions/public';
import { prepareWorkspaceSpaceRoutes } from '#layers/spaces/public';
import { hydrateWorkspaceSchema, workspaceDataSourcesHydration, workspaceInfoHydration, workspaceKeyStorageHydration, workspaceSettingsHydration } from '#layers/workspace/public';

/** What publication needs beyond the runtime itself: the router has no cache to derive routes from. */
export interface PreparedWorkspaceRoutes {
	routes: RouteRecordRaw[];
}

interface HydrateWorkspaceOptions extends HydrationContext {
	runtime: WorkspaceRuntime<WorkspaceEntityStore>;
	systemStore: SystemEntityStore;
	translate: (key: string) => string;
}

/**
 * Every commit-gating query a candidate runtime loads before it publishes; stores read them through `use()`.
 * Resolved per call rather than at import so a spec that mocks a layer barrel without these exports still boots the app.
 */
function workspaceHydrations(): readonly WorkspaceHydration[] {
	return [
		workspaceDataSourcesHydration,
		entitlementResourcesHydration,
		currentUserRolesHydration,
		workspaceInfoHydration,
		workspaceBlocksHydration,
		workspaceSettingsHydration,
		workspaceKeyStorageHydration,
		workspaceExtensionsHydration,
		workspaceDataConnectorsHydration,
	];
}

/**
 * Loads every commit-gating dependency into the candidate runtime; stores read those queries once the
 * runtime is active. Never resolve the ambient (still-active) workspace store here.
 */
export async function hydrateWorkspace({
	runtime,
	systemStore,
	translate,
	userId,
}: HydrateWorkspaceOptions): Promise<PreparedWorkspaceRoutes> {
	const context: HydrationContext = { userId };

	const [routes] = await Promise.all([
		prepareWorkspaceSpaceRoutes(runtime),
		requireWorkspaceRecord({ slug: runtime.slug, systemStore, translate }),
		...workspaceHydrations().map((hydration) => hydration.hydrate(runtime, context)),
	]);

	// Joining the settled query hands back its rows; registering the collections is the one step beyond loading.
	hydrateWorkspaceSchema({ sources: await workspaceDataSourcesHydration.hydrate(runtime, context), store: runtime.store });

	return { routes };
}

async function requireWorkspaceRecord({
	slug,
	systemStore,
	translate,
}: { slug: string; systemStore: SystemEntityStore; translate: (key: string) => string }) {
	const workspace = await systemStore.MonospaceWorkspace.findFirst({
		fetchPolicy: 'fetch-only',
		filter: { apiName: { _eq: slug } },
	});

	if (workspace) return;

	throw createError({
		statusCode: 404,
		statusMessage: translate('workspace.not_found'),
	});
}
